import { formatMinorAmount } from '@repo/common';
import { type BillingPlanSummaryQuery, DataTestIds } from '@repo/frontend-shared';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';

import { formatBillingDate } from '../billing-format';
import { RefundFact, RefundPackageSummary, RefundRuleFacts, RefundToValue } from './refund-eligibility-facts';
import { describeRefundReason } from './refund-format';
import { RefundPolicyLink } from './refund-policy-link';

type BillingPlanRefund = NonNullable<NonNullable<BillingPlanSummaryQuery['myBillingPlanSummary']>['refund']>;

interface RefundDetailsDialogProps {
  isOpen: boolean;
  productTitle: string;
  planLabel: string;
  refund: BillingPlanRefund;
  onClose(): void;
}

function DetailRow({ label, value, dataTestId }: { label: string; value: string; dataTestId?: string }) {
  return (
    <RefundFact label={label} dataTestId={dataTestId}>
      <SDSText type="body-large" text={value} color="text-secondary" />
    </RefundFact>
  );
}

/**
 * A refund that already happened, read-only.
 *
 * Deliberately not the request dialog in a "done" state: there is nothing to
 * decide here, so it has no second button. It still shows both refund rules,
 * as the frame does: the window as it stood when the refund happened, and the
 * completion rate the backend computes the same way eligibility does.
 */
export function RefundDetailsDialog({ isOpen, productTitle, planLabel, refund, onClose }: RefundDetailsDialogProps) {
  const reasonLabel = describeRefundReason(refund.reasonCode);

  return (
    <SDSDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Refund details"
      size="small"
      className="BillingDialog"
      dataTestId={DataTestIds.BILLING.REFUND.DETAILS_DIALOG}
      // Outlined, as drawn: closing is the only thing left to do, not a
      // decision the dialog is asking for.
      secondaryAction={{ label: 'Close', onClick: onClose }}
    >
      <SDSFlexbox direction="column" gap="no-design-24" fullWidth>
        <RefundPackageSummary productTitle={productTitle} planLabel={planLabel} />

        <SDSFlexbox direction="column" gap="input-regular" fullWidth>
          <RefundRuleFacts
            windowDays={refund.windowDays}
            daysRemaining={refund.daysRemaining}
            completionRatePercent={refund.completionRatePercent}
            completionLimitPercent={refund.completionLimitPercent}
          />
          <DetailRow
            label="Refund amount"
            value={formatMinorAmount(refund.amount.amountMinor, refund.amount.currency)}
          />
          {refund.refundToLabel ? (
            <RefundFact label="Refund to">
              <RefundToValue refundToLabel={refund.refundToLabel} />
            </RefundFact>
          ) : null}
          {reasonLabel ? (
            <DetailRow
              label="Refund reason"
              value={reasonLabel}
              dataTestId={DataTestIds.BILLING.REFUND.DETAILS_REASON}
            />
          ) : null}
          {refund.refundedAt ? (
            <DetailRow
              label="Refunded on"
              value={formatBillingDate(refund.refundedAt)}
              dataTestId={DataTestIds.BILLING.REFUND.DETAILS_REFUNDED_ON}
            />
          ) : null}
        </SDSFlexbox>

        <SDSFlexbox direction="row" className="RefundNotice" fullWidth>
          <SDSText type="body-large" text="Your refund has been processed." color="text-secondary" />
        </SDSFlexbox>

        <RefundPolicyLink />
      </SDSFlexbox>
    </SDSDialog>
  );
}
